import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import Brand from "@/components/Brand";
import PasswordInput from "@/components/PasswordInput";
import { LockKey, ArrowRight, ShieldCheck } from "@phosphor-icons/react";

// V1.1 #6 — Team Invite Email Flow
// Invitee lands here from the invite email. We verify the token, show
// their name + role, and let them set a password. On success we redirect
// to /login (they sign in with the new password).
export default function AcceptInvite() {
    const [params] = useSearchParams();
    const nav = useNavigate();
    const token = params.get("token");
    const [loading, setLoading] = useState(true);
    const [invite, setInvite] = useState(null);
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState("");

    useEffect(() => {
        if (!token) {
            setErr("Missing invite token.");
            setLoading(false);
            return;
        }
        api.get(`/invites/${token}`)
            .then((r) => setInvite(r.data))
            .catch((e) => setErr(formatApiError(e.response?.data?.detail) || "Invite link is invalid or expired."))
            .finally(() => setLoading(false));
    }, [token]);

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        if (password.length < 8) {
            setErr("Password must be at least 8 characters.");
            return;
        }
        if (password !== confirm) {
            setErr("Passwords don't match.");
            return;
        }
        setSaving(true);
        try {
            await api.post("/invites/accept", { token, password });
            // Google Ads — recruiter/team member accepted an invite = new
            // workspace user activated. Best proxy we have for "recruiter signup".
            try {
                const { fireGadsConversion } = await import("@/lib/gtagConversions");
                fireGadsConversion("recruiter_signup", { value: 500, currency: "INR" });
            } catch { /* best-effort */ }
            toast.success("Account activated. Please sign in with your new password.");
            nav("/login");
        } catch (e) {
            setErr(formatApiError(e.response?.data?.detail) || "Couldn't activate your account.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="min-h-screen bg-paper flex flex-col" data-testid="accept-invite-page">
            <header className="px-6 sm:px-10 py-6 border-b border-[#E5E5E5] bg-white">
                <Link to="/" data-testid="accept-invite-logo">
                    <Brand size="h-7" to={null} />
                </Link>
            </header>
            <main className="flex-1 flex items-center justify-center px-4 py-10 sm:py-16">
                <div className="w-full max-w-md bg-white border border-[#E5E5E5] p-6 sm:p-8">
                    <p className="text-[10px] font-black uppercase tracking-[0.25em] text-cobalt mb-3">
                        Team invite
                    </p>
                    <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter text-ink mb-2">
                        Set your password.
                    </h1>
                    <p className="text-sm text-[#525252] mb-6">
                        Almost there — pick a password and you'll be signed in as a teammate.
                    </p>

                    {loading && (
                        <p className="text-sm text-[#525252]" data-testid="accept-invite-loading">
                            Verifying your invite…
                        </p>
                    )}

                    {!loading && err && !invite && (
                        <div
                            data-testid="accept-invite-error"
                            className="border border-[#DC2626] bg-[#FEF2F2] text-[#DC2626] text-sm px-4 py-3 mb-4"
                        >
                            {err} <br />
                            <Link to="/login" className="underline font-bold mt-2 inline-block">
                                Go to login &rarr;
                            </Link>
                        </div>
                    )}

                    {!loading && invite && (
                        <>
                            <div className="bg-[#F5F5F5] border border-[#E5E5E5] px-4 py-3 mb-6 text-sm">
                                <p className="text-[10px] font-black uppercase tracking-wider text-[#A3A3A3] mb-1">
                                    You're joining as
                                </p>
                                <p className="font-bold text-ink" data-testid="invite-name">{invite.name}</p>
                                <p className="text-xs text-[#525252]" data-testid="invite-email">{invite.email}</p>
                                <span
                                    data-testid="invite-role"
                                    className="mt-2 inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider px-2 py-1 bg-white text-[#3730A3]"
                                >
                                    <ShieldCheck size={10} weight="fill" /> {invite.role.replace("_", " ")}
                                </span>
                            </div>
                            <form onSubmit={submit} className="space-y-4">
                                <div>
                                    <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#525252] mb-2 block">
                                        New password
                                    </label>
                                    <div className="relative">
                                        <LockKey
                                            size={16}
                                            className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A3A3A3] z-10 pointer-events-none"
                                            weight="bold"
                                        />
                                        <PasswordInput
                                            data-testid="invite-password-input"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            required
                                            minLength={8}
                                            autoComplete="new-password"
                                            placeholder="At least 8 characters"
                                            className="w-full bg-white border border-[#E5E5E5] pl-10 py-2.5 focus:outline-none focus:border-cobalt"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#525252] mb-2 block">
                                        Confirm password
                                    </label>
                                    <PasswordInput
                                        data-testid="invite-confirm-input"
                                        value={confirm}
                                        onChange={(e) => setConfirm(e.target.value)}
                                        required
                                        minLength={8}
                                        autoComplete="new-password"
                                        className="w-full bg-white border border-[#E5E5E5] px-4 py-2.5 focus:outline-none focus:border-cobalt"
                                    />
                                </div>
                                {err && (
                                    <div
                                        data-testid="accept-invite-form-error"
                                        className="border border-[#DC2626] bg-[#FEF2F2] text-[#DC2626] text-sm px-4 py-3"
                                    >
                                        {err}
                                    </div>
                                )}
                                <button
                                    type="submit"
                                    data-testid="accept-invite-submit"
                                    disabled={saving}
                                    className="w-full bg-cobalt hover:bg-cobalt-dark disabled:opacity-60 text-white px-6 py-3 font-bold tracking-wide text-sm inline-flex items-center justify-center gap-2"
                                >
                                    {saving ? "Activating…" : "Set password & continue"}
                                    <ArrowRight size={14} weight="bold" />
                                </button>
                            </form>
                        </>
                    )}
                </div>
            </main>
        </div>
    );
}
